/**
 * Utility functions for handling URL parameters for filter states
 */

// Constants for filter parameter names
export const FILTER_PARAM_NAMES = {
  SEARCH: "search",
  SCIENTIFIC_NAME: "scientific_name",
  GENUS: "genus",
  FAMILY: "family",
  ORDER: "order",
  CLASS: "class",
  PHYLUM: "phylum",
  CATEGORY: "category",
  PRODUCT: "product",
  BRAND: "brand",
  WATER_TYPES: "water_types",
  SORT: "sort",
  SIZE_MIN: "size_min",
  SIZE_MAX: "size_max",
  INCLUDE_NUMERIC_SIZES: "include_numeric_sizes",
  INCLUDE_NON_NUMERIC_SIZES: "include_non_numeric_sizes",
  NON_NUMERIC_SIZES: "non_numeric_sizes",
  FRESHWATER: "freshwater",
  SALTWATER: "saltwater",
  FAVORITES_ONLY: "favorites_only",
  NOTIFICATIONS_ONLY: "notifications_only",
  REEF_WIZARD_ONLY: "reef_wizard_only",
  REEF_WIZARD_TANK: "reef_wizard_tank",
  STORES: "stores",
  MIN_TANK_SIZE_MAX: "min_tank_size_max",
  MAX_SIZE_MAX: "max_size_max",
  TEMPERATURE_MIN: "temp_min",
  TEMPERATURE_MAX: "temp_max",
  PH_MIN: "ph_min",
  PH_MAX: "ph_max",
  CARE_LEVELS: "care_levels",
  REEF_SAFE: "reef_safe",
  PLANT_SAFE: "plant_safe",
  TEMPERAMENT: "temperament",
  DIET_TRAITS: "diet_traits",
  LIFESPAN_MIN: "lifespan_min",
  LIFESPAN_MAX: "lifespan_max",
  HAS_CARE_GUIDE: "has_care_guide",
  REEFCYCLOPEDIA_QUICK_FACTS: "reef_quick_facts",
};

// Array of all filter parameter names for easy iteration
export const ALL_FILTER_PARAM_NAMES = Object.values(FILTER_PARAM_NAMES);

const finiteNumericParam = (params, name) => {
  const raw = params.get(name);
  return raw !== null && raw.trim() !== "" && Number.isFinite(Number(raw)) ? Number(raw) : null;
};

// Array filters
// Array comparison helper - check if arrays have same elements regardless of order
const arraysEqual = (arr1, arr2) => {
  if (!arr1 && !arr2) return true;
  if (!arr1 || !arr2) return false;
  if (arr1.length !== arr2.length) return false;
  return (
    arr1.every((item) => arr2.includes(item)) &&
    arr2.every((item) => arr1.includes(item))
  );
};

const compactParamValues = (values) => {
  const valueList = Array.isArray(values) ? values : [values];
  return valueList
    .map((value) =>
      value === null || value === undefined ? "" : String(value).trim()
    )
    .filter(Boolean);
};

const encodeParamValues = (values) =>
  compactParamValues(values).map(encodeURIComponent).join(",");

const decodeParamValues = (rawValue) => {
  if (rawValue === null || rawValue === "") return [];
  return rawValue
    .split(",")
    .map((value) => decodeURIComponent(value))
    .filter(Boolean);
};

const serializeQuickFactFilters = (quickFactFilters = {}) =>
  Object.entries(quickFactFilters)
    .map(([key, values]) => [
      key,
      [...new Set((values || []).filter(Boolean))].sort(),
    ])
    .filter(([, values]) => values.length > 0)
    .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
    .map(([key, values]) => `${key}:${values.join("|")}`)
    .join(";");

const deserializeQuickFactFilters = (rawValue) => {
  if (!rawValue) return {};

  return rawValue
    .split(";")
    .reduce((filters, entry) => {
      const separatorIndex = entry.indexOf(":");
      if (separatorIndex === -1) {
        return filters;
      }

      const key = entry.slice(0, separatorIndex).trim();
      const values = entry
        .slice(separatorIndex + 1)
        .split("|")
        .map((value) => value.trim())
        .filter(Boolean);

      if (!key || values.length === 0) {
        return filters;
      }

      return {
        ...filters,
        [key]: [...new Set(values)].sort(),
      };
    }, {});
};

/**
 * Converts filter state to URL parameters
 * @param {Object} filters - Object containing all filter states
 * @param {Object} defaultValues - Default values for filters
 * @param {boolean} includeSizes - Whether to include size filters
 * @param {boolean} storesInitialized - Whether stores have been initialized
 * @returns {URLSearchParams} - URLSearchParams object
 */
export const filtersToUrlParams = (
  filters,
  defaultValues = {},
  includeSizes = false,
  storesInitialized = true
) => {
  const params = new URLSearchParams();

  // String filters
  if (filters.searchTerm)
    params.set(FILTER_PARAM_NAMES.SEARCH, filters.searchTerm);
  if (filters.scientificFilter)
    params.set(
      FILTER_PARAM_NAMES.SCIENTIFIC_NAME,
      encodeURIComponent(filters.scientificFilter)
    );
  if (filters.genusFilter)
    params.set(
      FILTER_PARAM_NAMES.GENUS,
      encodeURIComponent(filters.genusFilter)
    );
  if (filters.familyFilter)
    params.set(
      FILTER_PARAM_NAMES.FAMILY,
      encodeURIComponent(filters.familyFilter)
    );
  if (filters.orderFilter)
    params.set(
      FILTER_PARAM_NAMES.ORDER,
      encodeURIComponent(filters.orderFilter)
    );
  if (filters.classFilter)
    params.set(
      FILTER_PARAM_NAMES.CLASS,
      encodeURIComponent(filters.classFilter)
    );
  if (filters.phylumFilter)
    params.set(
      FILTER_PARAM_NAMES.PHYLUM,
      encodeURIComponent(filters.phylumFilter)
    );
  if (Array.isArray(filters.categoryFilter)) {
    const categoryFilters = compactParamValues(filters.categoryFilter);
    if (categoryFilters.length > 0) {
      params.set(
        FILTER_PARAM_NAMES.CATEGORY,
        encodeParamValues(categoryFilters)
      );
    }
  } else if (filters.categoryFilter) {
    params.set(FILTER_PARAM_NAMES.CATEGORY, filters.categoryFilter);
  }
  if (filters.productFilter)
    params.set(
      FILTER_PARAM_NAMES.PRODUCT,
      encodeURIComponent(filters.productFilter)
    );
  if (filters.brandFilter) {
    const brandFilters = compactParamValues(filters.brandFilter);
    if (brandFilters.length > 0) {
      params.set(FILTER_PARAM_NAMES.BRAND, encodeParamValues(brandFilters));
    }
  }
  if (Array.isArray(filters.waterTypeFilter)) {
    params.set(
      FILTER_PARAM_NAMES.WATER_TYPES,
      encodeParamValues(filters.waterTypeFilter)
    );
  } else if (filters.waterTypeFilter) {
    params.set(
      FILTER_PARAM_NAMES.WATER_TYPES,
      encodeParamValues(filters.waterTypeFilter)
    );
  }
  if (
    filters.sortOrder &&
    filters.sortOrder !== defaultValues.sortOrder
  ) {
    params.set(FILTER_PARAM_NAMES.SORT, filters.sortOrder);
  }

  if (includeSizes) {
    // Size filters
    if (
      filters.sizeMin !== undefined &&
      filters.sizeMin !== null &&
      filters.sizeMin !== "" &&
      filters.sizeMin !== defaultValues.minSize
    )
      params.set(FILTER_PARAM_NAMES.SIZE_MIN, filters.sizeMin);
    if (
      filters.sizeMax !== undefined &&
      filters.sizeMax !== null &&
      filters.sizeMax !== "" &&
      filters.sizeMax !== defaultValues.maxSize
    )
      params.set(FILTER_PARAM_NAMES.SIZE_MAX, filters.sizeMax);
    if (filters.includeNumericSizes === false)
      params.set(FILTER_PARAM_NAMES.INCLUDE_NUMERIC_SIZES, "false");
    if (filters.includeNonNumericSizes === false)
      params.set(FILTER_PARAM_NAMES.INCLUDE_NON_NUMERIC_SIZES, "false");
    if (
      filters.nonNumericSizeFilter &&
      !arraysEqual(filters.nonNumericSizeFilter, defaultValues.availableNonNumericSizes)
    )
      params.set(
        FILTER_PARAM_NAMES.NON_NUMERIC_SIZES,
        [...filters.nonNumericSizeFilter].sort().join(",")
      );
  }

  // Boolean filters (only set if false to keep URLs clean)
  if (filters.freshwaterFilter === false)
    params.set(FILTER_PARAM_NAMES.FRESHWATER, "false");
  if (filters.saltwaterFilter === false)
    params.set(FILTER_PARAM_NAMES.SALTWATER, "false");
  if (filters.favoritesOnly)
    params.set(FILTER_PARAM_NAMES.FAVORITES_ONLY, "true");
  if (filters.notificationsOnly)
    params.set(FILTER_PARAM_NAMES.NOTIFICATIONS_ONLY, "true");
  if (filters.reefWizardOnly)
    params.set(FILTER_PARAM_NAMES.REEF_WIZARD_ONLY, "true");
  if (filters.reefWizardTankFilter)
    params.set(FILTER_PARAM_NAMES.REEF_WIZARD_TANK, filters.reefWizardTankFilter);

  // Care requirement filters
  if (filters.minTankSizeMax !== undefined && filters.minTankSizeMax !== null)
    params.set(FILTER_PARAM_NAMES.MIN_TANK_SIZE_MAX, filters.minTankSizeMax);
  if (filters.maxSizeMax !== undefined && filters.maxSizeMax !== null)
    params.set(FILTER_PARAM_NAMES.MAX_SIZE_MAX, filters.maxSizeMax);
  if (filters.temperatureRange) {
    params.set(FILTER_PARAM_NAMES.TEMPERATURE_MIN, filters.temperatureRange[0]);
    params.set(FILTER_PARAM_NAMES.TEMPERATURE_MAX, filters.temperatureRange[1]);
  }
  if (filters.phRange) {
    params.set(FILTER_PARAM_NAMES.PH_MIN, filters.phRange[0]);
    params.set(FILTER_PARAM_NAMES.PH_MAX, filters.phRange[1]);
  }
  if (filters.careLevels?.length > 0)
    params.set(FILTER_PARAM_NAMES.CARE_LEVELS, filters.careLevels.join(","));
  if (filters.reefSafeValues?.length > 0)
    params.set(FILTER_PARAM_NAMES.REEF_SAFE, filters.reefSafeValues.join(","));
  if (filters.plantSafeValues?.length > 0)
    params.set(FILTER_PARAM_NAMES.PLANT_SAFE, filters.plantSafeValues.join(","));
  if (filters.temperamentValues?.length > 0)
    params.set(FILTER_PARAM_NAMES.TEMPERAMENT, filters.temperamentValues.join(","));
  if (filters.dietTraits?.length > 0)
    params.set(
      FILTER_PARAM_NAMES.DIET_TRAITS,
      [...new Set(filters.dietTraits)].sort().join(",")
    );
  if (filters.lifespanRange) {
    params.set(FILTER_PARAM_NAMES.LIFESPAN_MIN, filters.lifespanRange[0]);
    params.set(FILTER_PARAM_NAMES.LIFESPAN_MAX, filters.lifespanRange[1]);
  }
  if (filters.hasCareGuide && filters.hasCareGuide !== "all")
    params.set(FILTER_PARAM_NAMES.HAS_CARE_GUIDE, filters.hasCareGuide);

  const serializedQuickFactFilters = serializeQuickFactFilters(
    filters.quickFactFilters
  );
  if (serializedQuickFactFilters) {
    params.set(
      FILTER_PARAM_NAMES.REEFCYCLOPEDIA_QUICK_FACTS,
      serializedQuickFactFilters
    );
  }

  // Only apply stores filter if stores have been initialized
  if (
    storesInitialized &&
    !arraysEqual(filters.allowedStores, defaultValues.allowedStores)
  ) {
    params.set(
      FILTER_PARAM_NAMES.STORES,
      filters.allowedStores ? filters.allowedStores.join(",") : null
    );
  }

  return params;
};

/**
 * Converts URL parameters to filter state
 * @param {URLSearchParams} params - URLSearchParams object
 * @param {Object} defaultValues - Default values for filters
 * @returns {Object} - Object containing filter states
 */
export const urlParamsToFilters = (params, defaultValues = {}) => {
  const filters = {};

  // String filters
  filters.searchTerm = params.get(FILTER_PARAM_NAMES.SEARCH) || "";
  filters.scientificFilter = params.get(FILTER_PARAM_NAMES.SCIENTIFIC_NAME)
    ? decodeURIComponent(params.get(FILTER_PARAM_NAMES.SCIENTIFIC_NAME))
    : "";
  filters.genusFilter = params.get(FILTER_PARAM_NAMES.GENUS)
    ? decodeURIComponent(params.get(FILTER_PARAM_NAMES.GENUS))
    : "";
  filters.familyFilter = params.get(FILTER_PARAM_NAMES.FAMILY)
    ? decodeURIComponent(params.get(FILTER_PARAM_NAMES.FAMILY))
    : "";
  filters.orderFilter = params.get(FILTER_PARAM_NAMES.ORDER)
    ? decodeURIComponent(params.get(FILTER_PARAM_NAMES.ORDER))
    : "";
  filters.classFilter = params.get(FILTER_PARAM_NAMES.CLASS)
    ? decodeURIComponent(params.get(FILTER_PARAM_NAMES.CLASS))
    : "";
  filters.phylumFilter = params.get(FILTER_PARAM_NAMES.PHYLUM)
    ? decodeURIComponent(params.get(FILTER_PARAM_NAMES.PHYLUM))
    : "";
  const categoryParam = params.get(FILTER_PARAM_NAMES.CATEGORY) || "";
  filters.categoryFilter = categoryParam;
  filters.categoryFilters = decodeParamValues(
    params.get(FILTER_PARAM_NAMES.CATEGORY)
  );
  filters.productFilter = params.get(FILTER_PARAM_NAMES.PRODUCT)
    ? decodeURIComponent(params.get(FILTER_PARAM_NAMES.PRODUCT))
    : "";
  filters.brandFilters = decodeParamValues(params.get(FILTER_PARAM_NAMES.BRAND));
  filters.brandFilter = filters.brandFilters;
  filters.waterTypeFilter = params.has(FILTER_PARAM_NAMES.WATER_TYPES)
    ? decodeParamValues(params.get(FILTER_PARAM_NAMES.WATER_TYPES))
    : null;
  filters.waterTypeFilters = filters.waterTypeFilter;
  filters.sortOrder =
    params.get(FILTER_PARAM_NAMES.SORT) ||
    defaultValues.sortOrder ||
    "mostListings";
  const sizeMin = params.get(FILTER_PARAM_NAMES.SIZE_MIN);
  const sizeMax = params.get(FILTER_PARAM_NAMES.SIZE_MAX);
  filters.sizeMin = sizeMin !== null && Number.isFinite(Number(sizeMin))
    ? Number(sizeMin)
    : defaultValues.minSize ?? 0;
  filters.sizeMax = sizeMax !== null && Number.isFinite(Number(sizeMax))
    ? Number(sizeMax)
    : defaultValues.maxSize ?? 36;
  filters.includeNumericSizes =
    params.get(FILTER_PARAM_NAMES.INCLUDE_NUMERIC_SIZES) !== "false";
  filters.includeNonNumericSizes =
    params.get(FILTER_PARAM_NAMES.INCLUDE_NON_NUMERIC_SIZES) !== "false";
  const nonNumericSizes = params.get(FILTER_PARAM_NAMES.NON_NUMERIC_SIZES);
  filters.nonNumericSizeFilter = nonNumericSizes !== null
    ? nonNumericSizes.split(",").filter(Boolean)
    : defaultValues.availableNonNumericSizes || [];

  // Boolean filters
  filters.freshwaterFilter =
    params.get(FILTER_PARAM_NAMES.FRESHWATER) !== "false";
  filters.saltwaterFilter =
    params.get(FILTER_PARAM_NAMES.SALTWATER) !== "false";
  filters.favoritesOnly =
    params.get(FILTER_PARAM_NAMES.FAVORITES_ONLY) === "true";
  filters.notificationsOnly =
    params.get(FILTER_PARAM_NAMES.NOTIFICATIONS_ONLY) === "true";
  filters.reefWizardOnly =
    params.get(FILTER_PARAM_NAMES.REEF_WIZARD_ONLY) === "true";
  filters.reefWizardTankFilter =
    params.get(FILTER_PARAM_NAMES.REEF_WIZARD_TANK) || null;

  // Care requirement filters
  filters.minTankSizeMax = finiteNumericParam(params, FILTER_PARAM_NAMES.MIN_TANK_SIZE_MAX);
  filters.maxSizeMax = finiteNumericParam(params, FILTER_PARAM_NAMES.MAX_SIZE_MAX);
  const tempMin = finiteNumericParam(params, FILTER_PARAM_NAMES.TEMPERATURE_MIN);
  const tempMax = finiteNumericParam(params, FILTER_PARAM_NAMES.TEMPERATURE_MAX);
  filters.temperatureRange = tempMin !== null && tempMax !== null
    ? [tempMin, tempMax]
    : null;
  const phMin = finiteNumericParam(params, FILTER_PARAM_NAMES.PH_MIN);
  const phMax = finiteNumericParam(params, FILTER_PARAM_NAMES.PH_MAX);
  filters.phRange = phMin !== null && phMax !== null
    ? [phMin, phMax]
    : null;
  const careLevelsParam = params.get(FILTER_PARAM_NAMES.CARE_LEVELS);
  filters.careLevels = careLevelsParam ? careLevelsParam.split(",") : [];
  const reefSafeParam = params.get(FILTER_PARAM_NAMES.REEF_SAFE);
  filters.reefSafeValues = reefSafeParam ? reefSafeParam.split(",") : [];
  const plantSafeParam = params.get(FILTER_PARAM_NAMES.PLANT_SAFE);
  filters.plantSafeValues = plantSafeParam ? plantSafeParam.split(",") : [];
  const temperamentParam = params.get(FILTER_PARAM_NAMES.TEMPERAMENT);
  filters.temperamentValues = temperamentParam ? temperamentParam.split(",") : [];
  const dietTraitsParam = params.get(FILTER_PARAM_NAMES.DIET_TRAITS);
  filters.dietTraits = dietTraitsParam ? dietTraitsParam.split(",") : [];
  const lifespanMin = finiteNumericParam(params, FILTER_PARAM_NAMES.LIFESPAN_MIN);
  const lifespanMax = finiteNumericParam(params, FILTER_PARAM_NAMES.LIFESPAN_MAX);
  filters.lifespanRange = lifespanMin !== null && lifespanMax !== null
    ? [lifespanMin, lifespanMax]
    : null;
  filters.hasCareGuide = params.get(FILTER_PARAM_NAMES.HAS_CARE_GUIDE) || "all";
  filters.quickFactFilters = deserializeQuickFactFilters(
    params.get(FILTER_PARAM_NAMES.REEFCYCLOPEDIA_QUICK_FACTS)
  );

  // Array filters
  const stores = params.get(FILTER_PARAM_NAMES.STORES);
  filters.allowedStores =
    stores !== null
      ? stores
        ? stores.split(",")
        : []
      : defaultValues.allowedStores || [];

  return filters;
};
